import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useFileUploader } from '@vivli/shared/infrastructure/hook';
import { IUploader } from '@vivli/shared/features/data-package/infrastructure/interface';
import { Color } from '@vivli/shared/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';

const dragTextStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: 'white',
    marginBottom: '10px'
};

export const FileUploaderView = (props: IUploader) => {
    const {
        uploadUri,
        onFileUploadCompleted,
        onFileUploadError,
        onFileUploadStart,
        onFileUploadProgress,
        onAllFilesCompleted,
        onFileValidation,
        refreshApiToken,
        fileLimitReached,
        allowUpload,
        onUploaderInit
    } = props;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const showUploader = useMemo(() => !fileLimitReached && allowUpload, [fileLimitReached, allowUpload]);

    const uploaderApi = useFileUploader({
        endpoint: uploadUri,
        maxSimultaneousUploads: 3,
        getAuthToken: refreshApiToken || (() => Promise.resolve('')),
        events: {
            onFileValidation: (fileName) => {
                if (onFileValidation) {
                    return onFileValidation(fileName);
                }
                return true;
            },
            onProgress: (id, fileName, progress) => {
                onFileUploadProgress && onFileUploadProgress(id, fileName, progress);
            },
            onError: (id, fileName, error) => {
                onFileUploadError && onFileUploadError(id, fileName, error.message);
            },
            onComplete: (id, fileName, response) => {
                onFileUploadCompleted && onFileUploadCompleted(id, fileName);
            },
            onStart: (id, fileName, fileSizeKb) => {
                onFileUploadStart && onFileUploadStart(id, fileName, fileSizeKb);
            },
            onAllComplete: (succeeded, failed) => {
                onAllFilesCompleted && onAllFilesCompleted(succeeded as any, failed as any);
            }
        }
    });

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (selectedFiles) {
            uploaderApi.upload(selectedFiles);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if (fileLimitReached) return;

        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        if (fileLimitReached) return;

        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (fileLimitReached) return;

        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const droppedFiles = event.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            uploaderApi.upload(droppedFiles);
        }
    };

    const dragStyle: CSSProperties = {
        backgroundColor: 'transparent',
        textAlign: 'center',
        padding: '20px',
        border: isDragging ? '2px dashed #007bff' : '2px transparent solid',
        cursor: 'pointer',
        margin: '10px 0',
        color: Color.VIVLI_GREY,
        fontSize: '20px'
    };

    useEffect(() => {
        onUploaderInit && onUploaderInit(uploaderApi);
    }, []);

    return (
        <div style={dragStyle} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <input type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} ref={fileInputRef} />
            {showUploader && (
                <div style={dragTextStyle} onClick={() => fileInputRef.current?.click()} data-test-id="file-uploader-input">
                    <FontAwesomeIcon icon={faUpload} size="2x" color={Color.VIVLI_GREY} style={{ marginRight: '15px', fontSize: '18px' }} />
                    <div>Click or drag files here to upload.</div>
                </div>
            )}

            {props?.children}
        </div>
    );
};
